import React from 'react';
import PageShell from '../chrome/PageShell';
import EventListFrame from '../EventListFrame';
import useTopBarHeight from '../../hooks/useTopBarHeight';

/**
 * Shared list-page frame for /events/all and (Phase 8) /search.
 * docs/plans/2026-08-handoff-layout/03-see-all.md
 *
 * Banner with an eyebrow, a filter row that sticks under the 68px top bar, the
 * body, and an infinite-scroll sentinel. No load-more button anywhere.
 *
 * `layout`: 'cards' (the original 3-column ListCard grid, used by /search),
 * 'grid' / 'list' — the Aug 31 2026 events handoff bodies (EventListFrame), or
 * 'bare' — the children lay themselves out (/events/all's WallEventList).
 */
export default function ListPageLayout({
  eyebrow,
  title,
  subtitle,
  active = 'events',
  filters,
  shownLabel,
  children,
  sentinelRef,
  loading = false,
  hasMore = true,
  empty = null,
  isEmpty = false,
  layout = 'cards',
}) {
  const headerH = useTopBarHeight();
  return (
    <PageShell
      title={title}
      subtitle={subtitle}
      eyebrow={eyebrow}
      active={active}
      bare
      strip={(
        <div className="sticky z-30 border-b-2 border-black bg-white" style={{ top: headerH }}>
          <div className="mx-auto flex max-w-content flex-wrap items-center gap-3 px-4 py-3.5 sm:px-8">
            {filters}
            {shownLabel && (
              <span className="ml-auto font-mono text-[11px] uppercase tracking-[0.1em] text-hand-muted">
                {shownLabel}
              </span>
            )}
          </div>
        </div>
      )}
    >
      <main className="mx-auto w-full max-w-content px-4 pb-20 pt-7 sm:px-8">
        {isEmpty ? empty : (
          layout === 'bare' ? children
            : layout === 'cards'
              ? <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">{children}</div>
              : <EventListFrame view={layout}>{children}</EventListFrame>
        )}

        <div ref={sentinelRef} className="h-4" />
        {/* Loading is shown by skeleton items in the body now — only the end marker remains. */}
        {!isEmpty && !loading && !hasMore && (
          <div className="mt-8 flex h-[60px] items-center justify-center gap-3.5">
            <span className="font-mono text-[12px] font-bold uppercase tracking-[0.18em] text-hand-muted">End of list.</span>
            <span className="block h-2.5 w-2.5" style={{ background: '#E5E7EB' }} />
          </div>
        )}
      </main>
    </PageShell>
  );
}
